<!-- 講座サマリー -->
<script setup lang="ts">
  import { PlanInfo } from "~/entities/PlanInfo";
  import { useLoginStore } from "~/stores/LoginStore";
  import { usePlanStore } from "~/stores/PlanStore";

  interface Props {
    planInfo: PlanInfo;
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  const isLoading = ref(true);
  const planStore = usePlanStore();
  const loginStore = useLoginStore();

  // 体験談が行えるか判別用データを取得する
  if (props.planInfo?.isContract) {
    planStore.fetchReviewApplicable(props.planInfo.id);
  }

  // 体験談ボタンを表示するかどうか
  const showReviewButton = computed<boolean>(() =>
    planStore.isReviewable(props.planInfo, planStore.planReviewApplicable),
  );

  const planMemberNumber = computed<number>(() => {
    if (props.planInfo?.planMemberNumberLimit) {
      return Math.min(props.planInfo?.planMemberNumber, props.planInfo?.planMemberNumberLimit);
    }
    return props.planInfo?.planMemberNumber ?? 0;
  });
  const reviewsPath = computed<string>(() => `/plan/${props.planInfo?.id}/reviews`);

  onMounted(() => {
    try {
      // 口コミが行えるか判別用データを取得する
      if (props.planInfo?.isContract) {
        planStore.fetchReviewApplicable(props.planInfo.id);
      }
    } catch (error: any) {
      sentryErrorLog(`planStore.fetchReviewApplicable error : ${error}`);
    } finally {
      // 即isLoadingをfalseにするとチラつくため少し遅延させる
      setTimeout(() => {
        isLoading.value = false;
      }, 100);
    }
  });

  const isOwner = computed<boolean>(() => {
    if (props.planInfo?.isOwner) {
      return true;
    }
    return props.planInfo?.subOwnerIds.includes(loginStore.loginInfo.userId);
  });

  const canViewReviews = computed<boolean>(() => {
    if (!props.planInfo) {
      return false;
    }
    return props.planInfo.numberOfRate > 0 || isOwner.value;
  });
</script>

<template>
  <template v-if="!isLoading">
    <!-- 講座名 -->
    <div class="plan-title mb-2">{{ planInfo?.title }}</div>

    <!-- 体験談 -->
    <template v-if="planInfo?.isAvailableReview || isOwner">
      <div class="plan-rate-wrapper d-flex align-end">
        <span class="plan-rate-title">講座の体験談 : </span>
        <NuxtLink v-if="canViewReviews" class="cursor-pointer text-decoration-none" :to="reviewsPath">
          <span class="plan-rate-link ml-1">投稿数{{ numberFormat(planInfo?.numberOfRate) }}件</span>
        </NuxtLink>
        <span v-else class="plan-rate-novalue ml-1">体験談はまだありません</span>
      </div>
    </template>

    <!-- 会員数 -->
    <div v-if="!planInfo?.isHiddenMemberCount" class="plan-member-wrapper d-flex align-end mb-2">
      <span class="plan-member-number-title">講座の会員数 : </span>
      <span class="plan-member-number-value ml-1">
        {{ `${numberFormat(planMemberNumber)}名` }}
        {{ !!planInfo?.planMemberNumberLimit ? ` / ${numberFormat(planInfo?.planMemberNumberLimit)}名` : `` }}
      </span>
    </div>

    <div class="d-flex flex-column align-center">
      <!-- 体験談 ボタン -->
      <OrganismsPlanReviewButton v-if="showReviewButton" :plan-id="planInfo?.id" class="mt-2 mb-2" />
    </div>
  </template>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .plan-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: left;
    margin-bottom: 5px;
  }

  .plan-rate-title,
  .plan-member-number-title {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: left;
  }
  .plan-rate-link,
  .plan-rate-novalue,
  .plan-member-number-value {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: left;
  }
  .plan-rate-link {
    color: rgb(var(--v-theme-main-01));
  }

  .plan-rate-wrapper,
  .plan-member-wrapper {
    margin-bottom: 4px;
  }
  .cursor-pointer {
    cursor: pointer;
  }
</style>
